import { HOT_SPOTS, getCityById } from "@/config/locations";
import { getLandmark, landmarkName } from "@/lib/mobility/landmarks";
import { countryByCode } from "./place-slug";
import { ALL_DISTRICTS, ALL_LANDMARKS } from "@/config/districts";
import { placeById } from "./place-resolver";
import { canonicalPlaceId } from "@/config/search-places/areas";
import type { Locale } from "@/i18n/routing";

export type PlaceLocale = Locale;

type LocalizedName = { ko: string; en: string; ja?: string; "zh-CN"?: string; "zh-TW"?: string };

const pick = (name: LocalizedName, locale: PlaceLocale) =>
  name[locale] || name.ko || name.en;

/**
 * URL ?place=<지명 id> → 로케일 표시명. 모르는 id는 null.
 *
 * 순수 지명 검색은 상위 도시 슬러그(/s/seoul)로 접혀 지명이 경로에서 사라진다.
 * 그래서 id를 쿼리로 나르고 여기서 되돌린다. 로케일별 이름이 있는 사전(핫스팟·구·
 * 랜드마크)을 먼저 보고, 없으면 리졸버 라벨(수기 지명·역·도시)로 폴백한다.
 */
export function placeDisplayName(
  rawId: string | undefined | null,
  locale: PlaceLocale,
): string | null {
  if (!rawId) return null;
  const placeId = canonicalPlaceId(rawId); // 도시로 올라간 예전 area id
  const spot = HOT_SPOTS.find((s) => s.id === placeId);
  if (spot) return pick(spot.name, locale);
  const district = ALL_DISTRICTS.find((d) => d.id === placeId);
  if (district) return pick(district.name, locale);
  const landmark = ALL_LANDMARKS.find((l) => l.id === placeId);
  if (landmark) return pick(landmark.name, locale);
  // OSM 명소(검색바 위치 자동완성, 09-23) — id 는 "osm:<위키데이터 QID>".
  if (placeId.startsWith("osm:")) {
    const lm = getLandmark(placeId.slice(4));
    if (lm) return landmarkName(lm, locale);
  }
  const city = getCityById(placeId);
  if (city) return pick(city.name as LocalizedName, locale);
  const country = countryByCode(placeId);
  if (country) return pick(country.name as LocalizedName, locale);
  const resolved = placeById(placeId);
  if (!resolved) return null;
  return resolved.labels?.[locale] ?? resolved.label;
}
